import { useEffect, useRef } from 'react';

/** One clock controls dismissal and its visual progress, independent of hover or overlays. */
export function useSnackbarLifetime(duration: number | null, onExpire: () => void) {
  const barRef = useRef<HTMLDivElement>(null);
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;

  useEffect(() => {
    if (duration === null || duration <= 0) return;
    let remaining = duration;
    let startedAt = 0;
    let running = false;
    let finished = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let focused = document.hasFocus();
    const animation = barRef.current?.animate(
      [{ transform: 'scaleX(1)' }, { transform: 'scaleX(0)' }],
      { duration, fill: 'forwards', easing: 'linear' },
    );
    animation?.pause();

    const pause = () => {
      if (!running) return;
      remaining = Math.max(0, remaining - (performance.now() - startedAt));
      running = false;
      clearTimeout(timer);
      animation?.pause();
    };
    const resume = () => {
      if (running || finished || !focused || document.hidden) return;
      running = true;
      startedAt = performance.now();
      if (animation) {
        animation.currentTime = duration - remaining;
        animation.play();
      }
      timer = setTimeout(() => {
        running = false;
        finished = true;
        expireRef.current();
      }, remaining);
    };
    const blur = () => {
      focused = false;
      pause();
    };
    const focus = () => {
      focused = true;
      resume();
    };
    const visibility = () => {
      if (document.hidden) pause();
      else resume();
    };
    window.addEventListener('blur', blur);
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', visibility);
    resume();
    return () => {
      clearTimeout(timer);
      animation?.cancel();
      window.removeEventListener('blur', blur);
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [duration]);

  return barRef;
}
